/* eslint-disable @typescript-eslint/no-explicit-any */
// AllConectionsModal.tsx
import React, { useCallback, useMemo, useState } from "react";
import { defaultItemHref } from "./ConnectionCell";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import type { Fwaas, ConnectionRef } from "./FwaasData";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { ParsedFilter } from "kontrolEdge/helpers/advancedFilters";
import {
  ConnectionType,
  RENDER_ORDER,
  SECTION_TITLE,
  TAG_LABEL,
  SVC_ALIASES,
  ServiceTag,
  connectionNavId,
} from "./ConnectionsHelpers";

const SUGGESTED_KEYS = ["service", "subscriptionId", "id"]; // shows in typeahead

const norm = (x: unknown) => String(x ?? "").toLowerCase();

function matchOp(hay: string, needle: string, op?: ParsedFilter["op"]) {
  if (!needle) return true;
  return op === "=" ? hay === needle : hay.includes(needle);
}

/** ANDs all filters; if no filters, everything matches */
function matchesFilters(
  type: ConnectionType,
  item: ConnectionRef,
  filters: ParsedFilter[]
) {
  if (!filters.length) return true;

  const svcAliases = SVC_ALIASES[type];
  const svcText = [SECTION_TITLE[type], TAG_LABEL[type], ...svcAliases]
    .map(norm)
    .join(" ");

  const idText = norm(item.id);
  const subText = norm(item.subscriptionId);

  return filters.every((f) => {
    const key = f.key?.trim().toLowerCase();
    const value = norm(f.value);
    const op = f.op;

    if (!key) {
      return (
        matchOp(svcText, value, ":") ||
        matchOp(subText, value, ":") ||
        matchOp(idText, value, ":")
      );
    }

    if (key === "service" || key === "tag" || key === "type") {
      return (
        matchOp(svcText, value, op) ||
        matchOp(TAG_LABEL[type].toLowerCase(), value, op)
      );
    }

    if (key === "subscriptionid") {
      return matchOp(subText, value, op);
    }

    if (key === "id") {
      return matchOp(idText, value, op);
    }

    return false;
  });
}

type Props = {
  isOpen: boolean;
  row: Fwaas | null;
  onClose: () => void;
  onItemClick: (type: ConnectionType, id: string, row: Fwaas) => void;
  /** See `ConnectionsCell.itemHref`. */
  itemHref?: (type: ConnectionType, id: string, row: Fwaas) => string | null;
};

export default function AllConnectionsModal({
  isOpen,
  row,
  onClose,
  onItemClick,
  itemHref = defaultItemHref,
}: Props) {
  const [filters, setFilters] = useState<ParsedFilter[]>([]);

  const handleFiltersChange = useCallback((fs: ParsedFilter[]) => {
    setFilters(fs);
  }, []);

  const sections = useMemo(() => {
    if (!row) return [];
    return RENDER_ORDER.map((type) => {
      const list = (row.connections as any)?.[type] as
        | ConnectionRef[]
        | undefined;
      const all = Array.isArray(list) ? list : [];
      const filtered = all.filter((item) =>
        matchesFilters(type, item, filters)
      );
      return { type, all, filtered };
    }).filter((s) => s.filtered.length > 0);
  }, [row, filters]);

  if (!row) return null;

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="text-lg font-semibold text-semantic-text-primary">
          All connections — Service Id: {row.name ? row.name : "-"}
        </span>
      }
    >
      <div className="mb-4">
        <GenericSearchbar
          placeholder="Filter by service or subscriptionId"
          suggestedKeys={SUGGESTED_KEYS}
          onFiltersChange={handleFiltersChange}
          liveFiltering={true}
          debounceMs={200}
        />
      </div>

      <div
        className="space-y-4 pr-1"
        style={{ minHeight: "500px", maxHeight: "60vh", overflowY: "auto" }}
      >
        {sections.map(({ type, all, filtered }) => (
          <GenericCard
            key={type}
            isCollapsible={false}
            headerContent={
              <div className="flex items-center justify-between w-full">
                <p className="text-body-medium font-semibold">
                  {SECTION_TITLE[type]}
                </p>
                <span className="text-xs text-semantic-text-secondary">
                  {filtered.length}
                  {filtered.length !== all.length ? ` / ${all.length}` : ""}{" "}
                  item
                  {filtered.length === 1 ? "" : "s"}
                </span>
              </div>
            }
          >
            <div className="px-2 pb-2 pt-1">
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {filtered.map((item, idx) => {
                  const displayText = item.subscriptionId || item.id;
                  const navId = connectionNavId(type, item);
                  return (
                    <div
                      key={`${type}-${displayText}-${idx}`}
                      className="flex items-center gap-2"
                    >
                      <ServiceTag type={type} />
                      <button
                        type="button"
                        className="underline hover:no-underline focus:outline-none text-semantic-text-brand text-sm font-medium"
                        {...viewHref(itemHref(type, String(navId), row))}
                        onClick={() => onItemClick(type, String(navId), row)}
                        title={`${SECTION_TITLE[type]} ${displayText}`}
                      >
                        {displayText}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </GenericCard>
        ))}

        {sections.length === 0 && (
          <div
            className="flex items-center justify-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-sm text-semantic-text-secondary italic">
              No connections match your filters.
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <GenericButton
          size="sm"
          variant="secondary"
          onClick={onClose}
          label="Close"
        />
      </div>
    </GenericModal>
  );
}
